import axios from 'config/axios';
import store from 'store';
import { CLEAR_USER, SET_CURRENT_STUDENT, SET_LEARNING_SUPPORT } from 'store/actions/actionTypes';
import { ampli } from 'ampli';
import { licenseTypes, roleTypes } from 'constants/options';
import { parseJwt } from 'utils';
import setAuthToken from './setAuthToken';
import { LSGO_AUTHENTICATION_BASE_URL, LSGO_AUTHENTICATION_CLIENT_ID } from 'config/lsgoAuthentication';
import {
    refreshLsgoAuthenticationToken,
    scheduleLsgoAuthenticationRefresh,
    signOutLsgoAuthentication,
} from './lsgoAuthenticationTokenRefresh';

/**
 * The student's lsgo-authentication session in this app (lsgo-teacher's
 * lsgoAuthenticationSilentSession.js, adapted):
 *
 * - The access token goes where the legacy token goes (setAuthToken: axios
 *   headers + localStorage `authToken`), so existing screens work unchanged.
 * - The refresh token stays in auth's HttpOnly cookie; the access token is
 *   refreshed shortly before it expires.
 * - localStorage `lsgoAuthSession` = 'true' marks the session as an auth one,
 *   so boot restores it through auth and renewal / logout skip legacy.
 * - Only accounts whose token roles include 'student' are let in.
 */

const SESSION_KEY = 'lsgoAuthSession';
const REFRESH_LEAD_SECONDS = 15;
const REFRESH_RETRY_MS = 30 * 1000;
const SIGN_OUT_TIMEOUT_MS = 3000;

let cancelRefresh = null;

export class NotAStudentError extends Error {
    constructor() {
        super('This sign-in is for students. Teachers and school staff sign in at the teacher app.');
        this.name = 'NotAStudentError';
    }
}

/** True when this browser's session was signed in through lsgo-authentication. */
export function isLsgoAuthenticationSession() {
    try {
        return localStorage.getItem(SESSION_KEY) === 'true';
    } catch {
        return false;
    }
}

function setSessionMarker(on) {
    try {
        if (on) localStorage.setItem(SESSION_KEY, 'true');
        else localStorage.removeItem(SESSION_KEY);
    } catch {
        // Storage unavailable: the session lasts for this page only.
    }
}

/** True if the token's roles (for its school) include 'student'. */
export function isStudentClaims(claims) {
    return Array.isArray(claims?.roles) && claims.roles.includes('student');
}

function cancelScheduledRefresh() {
    if (cancelRefresh) cancelRefresh();
    cancelRefresh = null;
}

function scheduleRefresh(expiresInSeconds) {
    cancelScheduledRefresh();
    if (!expiresInSeconds) return;
    cancelRefresh = scheduleLsgoAuthenticationRefresh({
        expiresInSeconds,
        leadSeconds: REFRESH_LEAD_SECONDS,
        onDue: refreshSession,
    });
}

async function refreshSession() {
    cancelRefresh = null;
    try {
        const data = await refreshLsgoAuthenticationToken({
            authBaseUrl: LSGO_AUTHENTICATION_BASE_URL,
            clientId: LSGO_AUTHENTICATION_CLIENT_ID,
        });
        setAuthToken(data.access_token);
        scheduleRefresh(data.expires_in);
    } catch (err) {
        const status = Number(err?.response?.status);
        if (status === 400 || status === 401) {
            // The session ended at auth (revoked, expired, signed out elsewhere).
            clearLocalSession();
            store.dispatch({ type: CLEAR_USER, payload: '' });
            window.location.assign('/login');
            return;
        }
        // Network or server trouble: try again shortly.
        const timerId = setTimeout(refreshSession, REFRESH_RETRY_MS);
        cancelRefresh = () => clearTimeout(timerId);
    }
}

function clearLocalSession() {
    cancelScheduledRefresh();
    setSessionMarker(false);
    setAuthToken(false);
    localStorage.removeItem('authToken');
}

/**
 * Sets up the app's session from a /token response ({ access_token,
 * expires_in }): the student's profile from the token's legacy_user_id into
 * the store, the token on every request, and the next refresh. Throws
 * NotAStudentError (nothing set up) when the token's roles are not a
 * student's, and Error when the profile can't be loaded.
 */
export async function establishLsgoAuthenticationSession({ access_token: accessToken, expires_in: expiresIn }) {
    const claims = await parseJwt(accessToken || '').catch(() => null);
    if (!claims?.legacy_user_id) throw new Error('This account has no linked LSGO profile.');
    if (!isStudentClaims(claims)) throw new NotAStudentError();

    setAuthToken(accessToken);
    setSessionMarker(true);

    const profile = await axios.get(`/api/v4/users/${encodeURIComponent(claims.legacy_user_id)}`).catch(() => null);
    if (!profile?.data?.id) {
        clearLocalSession();
        throw new Error('We could not load your profile. Please try again.');
    }

    store.dispatch({ type: SET_CURRENT_STUDENT, payload: { ...profile.data } });
    store.dispatch({ type: SET_LEARNING_SUPPORT, payload: profile.data.learningSupport === 1 });
    try {
        ampli.identify(profile.data.id, {
            license: licenseTypes[profile.data.user_groups?.licenseId],
            role: roleTypes[profile.data.user_groups?.roleId],
            payment_status: profile.data.user_groups?.paymentStatus || 'free',
            group_id: profile.data.user_groups?.groupId,
            school_id: profile.data.schoolId,
        });
    } catch {
        // Analytics must never block sign-in.
    }

    scheduleRefresh(expiresIn);
    return profile.data;
}

/**
 * Ends the lsgo-authentication session: stops refreshing, clears the marker
 * and revokes the refresh cookie (and every access token from it). Capped so
 * an unreachable auth service can't hang sign-out. The caller clears the
 * rest of the app's state (logOutUser).
 */
export async function endLsgoAuthenticationSession() {
    cancelScheduledRefresh();
    setSessionMarker(false);
    let timerId;
    await Promise.race([
        signOutLsgoAuthentication({ authBaseUrl: LSGO_AUTHENTICATION_BASE_URL }).catch(() => {}),
        new Promise((resolve) => {
            timerId = setTimeout(resolve, SIGN_OUT_TIMEOUT_MS);
        }),
    ]);
    clearTimeout(timerId);
}

/**
 * Restores an lsgo-authentication session on boot (page load, new tab) from
 * the refresh cookie the browser already holds: no redirect. Resolves true
 * if the student is signed in again; false (and nothing left behind)
 * otherwise. A restored session that isn't a student's is signed out.
 */
export async function restoreLsgoAuthenticationSession() {
    let data;
    try {
        data = await refreshLsgoAuthenticationToken({
            authBaseUrl: LSGO_AUTHENTICATION_BASE_URL,
            clientId: LSGO_AUTHENTICATION_CLIENT_ID,
        });
    } catch {
        setSessionMarker(false);
        return false;
    }
    try {
        await establishLsgoAuthenticationSession(data);
        return true;
    } catch (err) {
        if (err instanceof NotAStudentError) await endLsgoAuthenticationSession();
        clearLocalSession();
        return false;
    }
}
